import React, {
    useMemo, useCallback,
} from 'react'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    LongTermLeaseFeeStatus,
} from 'app/enums/label.enums'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import Button from 'app/shared-components/Button'
import {
    ChangeValidityConfirmationDialog,
    useChangeValidity,
} from 'app/shared-components/ChangeValidity'
import {
    ArchiveButton, ArchiveConfirmationDialog, useArchive,
} from 'app/shared-components/Archive'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import useHasPermission from 'app/hooks/useHasPermission'
import useContractBasisContext from 'app/Apps/Pricing/ContractBasis/hooks/useContractBasisContext'
import useRequestWithConfirmation from 'app/hooks/useRequestWithConfirmation'

import {
    useCollapsibleTableContext,
} from 'app/shared-components/CollapsibleTable'
import LongTermLeaseFeesRequests from 'app/requests/ContractBasis/LongTermLeaseFees.requests'
import useRequest, {
    RequestFn, RequestKeys,
} from 'app/hooks/useRequest'

import LongTermLeaseFeesForm from '../LongTermLeaseFeesForm'
import {
    LongTermLeaseFeeObject,
} from '../LongTermLeaseFees.types'

enum ActionButtons {
    SetToValid = 'Set to Valid',
    SetToCancelled = 'Set to Cancelled',
    ChangeValidity = 'Change Validity',
    Archive = 'Archive',
}

export default function LongTermLeaseFeesUpdate({
    id, refetch,
}: Readonly<{
    id: number,
    refetch: (...args: any[]) => void
}>) {
    const {
        ContractBasis: ContractBasisRoutes,
    } = usePricingRoutes()

    const contractBasisUpdatePermission = useHasPermission(['contract_basis_update'])

    const {
        updatePathWithParams,
    } = useCollapsibleTableContext()

    const {
        contractId,
    } = useContractBasisContext()

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useRequest<LongTermLeaseFeeObject>({
        key: RequestKeys.getLongTermLeaseFees,
        params: {
            id,
        },
        requestFunc: RequestFn.getEntity(LongTermLeaseFeesRequests.get),
        enabled: Boolean(id),
    })

    const onSuccess = useCallback(({
        id: newId,
    }) => {
        if (newId) {
            updatePathWithParams({
                id: newId,
            })
        }
        invalidate()
        refetch()
    }, [
        invalidate,
        updatePathWithParams,
        refetch,
    ])

    const onCancel = useCallback(() => {
        updatePathWithParams({
            id: data?.id,
        })
        invalidate()
    }, [
        invalidate,
        data?.id,
        updatePathWithParams,
    ])

    const onChangeValiditySuccess = useCallback(() => {
        refetch()
        invalidate()
        ContractBasisRoutes.LongTermLeaseFees.navigate({
            params: {
                key: contractId,
                id: data?.id,
            },
        })
    }, [
        refetch,
        invalidate,
        ContractBasisRoutes.LongTermLeaseFees,
        contractId,
        data?.id,
    ])

    const changeValidity = useChangeValidity({
        requestParamFn: LongTermLeaseFeesRequests.changeValidity,
        notificationId: 'LongTermLeaseFeesUpdateDto',
        title: 'Long-term Lease Fee',
        data: {
            id: data?.id,
            validFrom: data?.validFrom,
            validTo: data?.validTo,
        },
        onSuccess: onChangeValiditySuccess,
    })

    const onCancelFeeSuccess = useCallback(() => {
        onSuccess({
            id: data?.id,
        })
        return ContractBasisRoutes.LongTermLeaseFees.navigate({
            params: {
                key: contractId,
                id: data?.id,
            },
        })
    }, [
        contractId,
        onSuccess,
        data,
        ContractBasisRoutes,
    ])

    const cancelFee = useRequestWithConfirmation({
        requestParamFn: LongTermLeaseFeesRequests.cancellation,
        notificationId: 'LongTermLeaseFeesUpdateDto',
        title: 'Long-term Lease Fee',
        data,
        actionDone: 'Cancelled',
        onSuccess: onCancelFeeSuccess,
    })

    const validFee = useRequestWithConfirmation({
        requestParamFn: LongTermLeaseFeesRequests.validation,
        notificationId: 'LongTermLeaseFeesUpdateDto',
        title: 'Long-term Lease Fee',
        data,
        actionDone: 'Validated',
        onSuccess: onCancelFeeSuccess,
    })

    const archive = useArchive({
        requestParamFn: data?.archived ? LongTermLeaseFeesRequests.restore
            : LongTermLeaseFeesRequests.archive,
        notificationId: 'LongTermLeaseFeesUpdateDto',
        title: 'Long-term Lease Fee',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: ContractBasisRoutes.LongTermLeaseFees.path,
            params: {
                key: contractId,
            },
        }),
        onSuccess: refetch,
    })

    const customButtons = useMemo(() => {
        return {
            [ActionButtons.SetToValid]: <Button
                key={ActionButtons.SetToValid}
                label={ActionButtons.SetToValid}
                onClick={validFee?.askConfirmation}
            />,
            [ActionButtons.SetToCancelled]: <Button
                key={ActionButtons.SetToCancelled}
                label={ActionButtons.SetToCancelled}
                onClick={cancelFee?.doAction}
            />,
            [ActionButtons.ChangeValidity]: <Button
                key={ActionButtons.ChangeValidity}
                label={ActionButtons.ChangeValidity}
                onClick={changeValidity.handleChangeValidity}
            />,
            [ActionButtons.Archive]: <ArchiveButton
                key="archive-button"
                onClick={archive.handleArchiveRestore}
                archived={data?.archived}
            />,
        }
    }, [
        archive.handleArchiveRestore,
        cancelFee?.doAction,
        changeValidity.handleChangeValidity,
        data?.archived,
        validFee?.askConfirmation,
    ])

    const customButtonsByStatus = useMemo(() => {
        return {
            [LongTermLeaseFeeStatus.IN_PROGRESS]: [
                customButtons[ActionButtons.SetToValid],
                customButtons[ActionButtons.SetToCancelled],
            ],
            [LongTermLeaseFeeStatus.VALID]: [
                customButtons[ActionButtons.SetToCancelled],
                customButtons[ActionButtons.ChangeValidity],
            ],
            [LongTermLeaseFeeStatus.CANCELLED]: [customButtons[ActionButtons.Archive]],
        }
    }, [customButtons])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <LongTermLeaseFeesForm
                    disabled={!contractBasisUpdatePermission}
                    value={data}
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    requestParams={LongTermLeaseFeesRequests.update({
                        id,
                    })}
                    customButtons={customButtonsByStatus[data?.longTermLeaseFeesStatus]}
                />
                <ChangeValidityConfirmationDialog
                    title="Long-term Lease Fee"
                    openConfirmDialog={changeValidity?.openConfirmDialog}
                    handleClose={changeValidity?.handleChangeValidityConfirm}
                    changeValidityState={changeValidity?.changeValidityState}
                    setChangeValidityState={changeValidity?.setChangeValidityState}
                    orderCount={null}
                />
                <ArchiveConfirmationDialog
                    title="Long-term Lease Fee"
                    openConfirmDialog={archive.openConfirmDialog}
                    handleClose={archive.handleArchiveRestoreConfirm}
                    archived={data?.archived}
                />
                <ConfirmationDialog
                    open={validFee.openConfirmDialog}
                    handleClose={validFee.doAction}
                    dialogTitle="VALIDATE LONG-TERM LEASE FEE CONFIRMATION"
                    dialogContent="Do you want to validate this Long-term Lease Fee?"
                />
            </>
        </StatusHandler>
    )
}
